import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MailCheck, RefreshCw, Copy, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Pill } from "@/components/hub/Common";

const MSG = { pending: "adm.em_pending", not_started: "adm.em_pending", temporary_failure: "adm.em_pending", failed: "adm.em_failed",
  missing: "adm.em_missing", unreachable: "adm.em_unreachable", not_configured: "adm.em_missing" };

const CopyCell = ({ v, testid }) => {
  const { t } = useI18n();
  return (
    <button onClick={() => navigator.clipboard.writeText(v).then(() => toast.success(t("adm.em_copied"))).catch(() => {})} data-testid={testid}
      className="group flex items-center gap-2 text-left font-mono text-xs text-slate-200 hover:text-white break-all">
      <span>{v}</span><Copy className="w-3.5 h-3.5 shrink-0 text-slate-500 group-hover:text-[#3ad7ff]" />
    </button>
  );
};

export const EmailTab = () => {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/email-domain").then((r) => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load]);
  const verify = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/email-domain/verify"); setD(r.data); toast.success(t("adm.em_verified")); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (!d) return <p className="text-sm text-slate-500" data-testid="admin-email-loading">…</p>;
  return (
    <div className="space-y-5" data-testid="admin-email">
      <div className="cx-card cx-brackets rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className={`w-14 h-14 rounded-2xl grid place-items-center shrink-0 ${d.active ? "bg-[#22e3a1]/15" : "bg-[#ffc94d]/10"}`}>
          {d.active ? <MailCheck className="w-7 h-7 text-[#22e3a1]" /> : <Clock className="w-7 h-7 text-[#ffc94d]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-white">{t("adm.em_title")} · <span className="font-mono text-[#3ad7ff]">{d.domain}</span></div>
          <div className="mt-2" data-testid="admin-email-status">
            {d.active ? <Pill tone="green">{t("adm.em_active").replace("{sender}", d.sender)}</Pill> : <Pill tone={d.status === "failed" ? "rose" : "amber"}>{t(MSG[d.status] || "adm.em_pending")}</Pill>}
          </div>
          <p className="text-xs text-slate-400 mt-3 max-w-2xl">{t("adm.em_intro").replace("{sender}", d.sender)}</p>
        </div>
        {!d.active && (
          <button onClick={verify} disabled={busy} className="cx-btn rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 shrink-0 disabled:opacity-50" data-testid="admin-email-verify-btn">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />{t("adm.em_verify")}
          </button>
        )}
      </div>
      {d.records.length > 0 && (
        <div className="cx-card rounded-2xl overflow-hidden" data-testid="admin-email-records">
          <div className="px-5 py-4 text-sm text-white font-semibold">{t("adm.em_records")}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-500 text-left">
                {["type", "name", "value", "prio", "status"].map((k) => <th key={k} className="px-5 py-2 font-normal">{t(`adm.em_${k}`)}</th>)}
              </tr></thead>
              <tbody>
                {d.records.map((r, i) => (
                  <tr key={i} className="border-t border-white/[0.05] align-top" data-testid={`admin-email-record-${i}`}>
                    <td className="px-5 py-3 font-mono text-xs text-[#c79bff]">{r.type}</td>
                    <td className="px-5 py-3"><CopyCell v={r.name || "@"} testid={`admin-email-record-name-${i}`} /></td>
                    <td className="px-5 py-3 max-w-[420px]"><CopyCell v={r.value} testid={`admin-email-record-value-${i}`} /></td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-400">{r.priority ?? "—"}</td>
                    <td className="px-5 py-3"><Pill tone={r.status === "verified" ? "green" : r.status === "failed" ? "rose" : "amber"}>{r.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-5 py-4 text-[11px] text-slate-500 border-t border-white/[0.05]">{t("adm.em_tip")}</p>
        </div>
      )}
    </div>
  );
};
